import React, { useState } from 'react';
import { Card, Dropdown, Modal, Space, Row, Segmented } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import {AddCircleRounded, CreateNewFolderRounded, ScaleRounded, BlenderRounded, ExpandMoreRounded } from '@mui/icons-material';
import TickerSearch from '../../../modules/Search/TickerSearch/TickerSearch';
import PortfolioSearch from '../../../modules/Search/PortfolioSearch/PortfolioSearch';
import ScaleEntry from './ScaleEntry';
import {
    Lock,
    LockOpen,
    ArrowUpRight,
    ArrowDownRight,
    PlusCircle,
    Ellipsis,
    LucideMenu,
    BookmarkPlus,
    FolderPlus,
    ListPlus,
    Disc,
} from 'lucide-react';
import { PlusOutlined, FolderAddOutlined } from '@ant-design/icons';
import RichSlider from "../../../components/Inputs/RichSlider";
import {updatePrecision} from "../../../store/slices/builder.slice";


const PrecisionSelector = ({ onPrecisionChange }) => {
  const options = [
    { value: 2, label: '2' },
    { value: 3, label: '3' },
    { value: 4, label: '4' },
    { value: 5, label: '5' }
  ];

  return (
      <Segmented
        options={options}
        onChange={onPrecisionChange}
      />
  );
};

const getAllowedTypes = (builderType) => {
    if (builderType === 'mm') {
        return ['b', 's', 'm'];
    }
    if (builderType === 'm') {
        return ['b', 's'];
    }
    if (builderType === 's') {
        return ['b'];
    }
    return [];
};

const BuilderToolbar = ({
    portfolioType,
    onEqualWeight,
    onLockAll,
    onUnlockAll,
    onProRataFill,
    onScaleUp,
    onScaleDown,
    onTickerSearch,
    onPortfolioSelection,
    onFetchBlendedTimeseries,
}) => {
    const dispatch = useDispatch();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalContent, setModalContent] = useState(null);
    const [currentValue, setCurrentValue] = useState(1);
    const [modalTitle, setModalTitle] = useState('');
    const [scaleAction, setScaleAction] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuType, setMenuType] = useState(null);
    const precision = useSelector(state => state.builder.precision);


    const showModalForScale = (action) => {
        setModalTitle('Proportional Scaler');
        setIsModalVisible(true);
        setModalContent('scale');
        setScaleAction(() => (value) => action(value));
    };

    const showModalForAction = (actionType) => {
        setModalTitle(actionType === 'tickerSearch' ? 'Search for Tickers' : 'Search your Portfolios');
        setIsModalVisible(true);
        setModalContent(actionType);
    };

    const handleOk = (value) => {
        if (modalContent === 'scale' && scaleAction) {
            scaleAction(value || currentValue);
        }
        setIsModalVisible(false);
        setCurrentValue(1);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
    };

    const handleMenuOpen = (event, type) => {
        setAnchorEl(event.currentTarget);
        setMenuType(type);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuType(null);
    };


  const handleUpdatePrecision = (newValue) => {
    dispatch(updatePrecision(newValue));
  }

    const renderModalContent = () => {
        switch (modalContent) {
            case 'tickerSearch':
                return <TickerSearch onTickersSubmit={onTickerSearch} />;
            case 'portfolioSelection':
                return (
                    <PortfolioSearch
                        onSelect={onPortfolioSelection}
                        allowTypes={getAllowedTypes(portfolioType)}
                        maxSelected={10}
                        bodyWidth={500}
                    />
                );
            case 'scale':
                return <ScaleEntry currentValue={currentValue} onSubmit={handleOk} />;
            default:
                return null;
        }
    };
    const items = [
        {
            key: '1',
            label: 'Add Ticker',
            onClick: () => showModalForAction('tickerSearch')
        },
        {
            type: 'divider'
        }
    ];

    // Conditionally add the "Add Sleeve" option
    if (portfolioType !== 'b') {
        items.push({
            key: '2',
            label: 'Add Sleeve',
            onClick: () => showModalForAction('portfolioSelection')
        });
    }


    const weightSchemeItems = [
        {
            key: '1',
            label: 'Equal Weights',
            onClick: onEqualWeight
        },
        {
            type: 'divider'
        },
        {
            key: '2',
            label: 'Pro-Rata to 100%',
            onClick: onProRataFill
        }
    ]

    return (
        <Row justify={'space-between'} className={'px-2 py-3 rounded-sm mb-2'} wrap={false}>
            <Space>
                <Dropdown
                    menu={{ items }}
                    placement="bottomLeft"
                    trigger={['hover']}
                >
                    <Button variant={'text'} startIcon={<PlusCircle strokeWidth={1} size={20}/>}>
                        <span className={'uppercase'}>
                            Add Holdings
                        </span>
                    </Button>
                </Dropdown>
                <Dropdown menu={{ items: weightSchemeItems }}>
                    <Button
                        variant={'text'}
                        onMouseEnter={(e) => handleMenuOpen(e, 'weightOptions')}
                        startIcon={<LucideMenu size={20} />}
                        size={'small'}
                    >
                        WEIGHT SCHEMES
                    </Button>
                </Dropdown>
                <PrecisionSelector onPrecisionChange={handleUpdatePrecision} />
            </Space>
            <Space>
                {/* Weight Options Dropdown */}
                {/*<Menu*/}
                {/*    anchorEl={anchorEl}*/}
                {/*    open={menuType === 'weightOptions'}*/}
                {/*    onClose={handleMenuClose}*/}
                {/*    items={weightSchemeItems}*/}
                {/*    MenuListProps={{ onMouseLeave: handleMenuClose }}*/}
                {/*>*/}

                {/*</Menu>*/}
                <ButtonGroup >
                    <Button color={'secondary'} onClick={onLockAll} size={'small'}>
                        <Lock size={18}/>
                    </Button>

                    <Button color={'secondary'} onClick={onUnlockAll} size={'small'}>
                        <LockOpen size={18}/>
                    </Button>
                    <Button color={'secondary'} onClick={() => { showModalForScale(onScaleUp); }} size={'small'}>
                        <ArrowUpRight size={20}/>
                    </Button>

                    <Button color={'secondary'} onClick={() => { showModalForScale(onScaleDown); }} size={'small'}>
                        <ArrowDownRight size={20}/>
                    </Button>
                </ButtonGroup>
                {/* Scale Options Dropdown */}
                <Button
                    color={'secondary'}
                    onMouseEnter={(e) => handleMenuOpen(e, 'timeseriesOptions')}
                    startIcon={<Ellipsis size={20} />}
                >
                    Timeseries Options
                </Button>
                <Menu
                    anchorEl={anchorEl}
                    open={menuType === 'timeseriesOptions'}
                    onClose={handleMenuClose}
                    MenuListProps={{ onMouseLeave: handleMenuClose }}
                >
                    <MenuItem onClick={() => { handleMenuClose(); onFetchBlendedTimeseries(); }}>Blend Pro Rata</MenuItem>
                </Menu>

            </Space>

            <Modal
                width={960}
                title={modalTitle}
                open={isModalVisible}
                onOk={handleOk}
                onCancel={handleCancel}
                footer={null}
            >
                <Card bordered={false}>{renderModalContent()}</Card>
            </Modal>
        </Row>
    );
};

export default BuilderToolbar;
